import { format } from 'date-fns';
import { DateFormatSetting, TimeFormatSetting, TimezoneSetting } from '../models/graphql';
import { useSettings } from './useSettings';
import { useCallback } from 'react';
import { useSelectedOrganization } from './useSelectedOrganization';
import { convertTimezoneEnumToName, convertUtcToTimezonedDate } from '../helpers/timeFormatter';
import { DateTimeStringFormat } from '../constants/time-formats';
import { TDateTimeString } from '../models/date';
import { TimezoneNames } from '../constants/timezones';

export enum DateStyle {
  DATE_ONLY = 'DATE_ONLY',
  DATE_ONLY_DOTTED = 'DATE_ONLY_DOTTED',
  TIME_ONLY = 'TIME_ONLY',
  DATE_ONLY_SHORT_MONTH = 'DATE_ONLY_SHORT_MONTH',
  WITH_DAY_OF_WEEK = 'WITH_DAY_OF_WEEK',
  FULL_FORMAT = 'FULL_FORMAT'
}

export type FormatStringType = {
  [DateStyle.DATE_ONLY]: Record<DateFormatSetting, string>;
  [DateStyle.DATE_ONLY_DOTTED]: Record<DateFormatSetting, string>;
  [DateStyle.TIME_ONLY]: Record<TimeFormatSetting | 'twelveHoursWithoutMinutes', string>;
  [DateStyle.DATE_ONLY_SHORT_MONTH]: Record<DateFormatSetting, string>;
  [DateStyle.WITH_DAY_OF_WEEK]: Record<DateFormatSetting, string>;
  [DateStyle.FULL_FORMAT]: string;
};

export const FormatString: FormatStringType = {
  [DateStyle.DATE_ONLY]: {
    [DateFormatSetting.DAY_MONTH_YEAR]: 'dd/MM/yy',
    [DateFormatSetting.MONTH_DAY_YEAR]: 'MM/dd/yy'
  },
  [DateStyle.DATE_ONLY_DOTTED]: {
    [DateFormatSetting.DAY_MONTH_YEAR]: 'dd.MM.yy',
    [DateFormatSetting.MONTH_DAY_YEAR]: 'MM.dd.yy'
  },
  [DateStyle.TIME_ONLY]: {
    twelveHoursWithoutMinutes: 'h a',
    [TimeFormatSetting.HOUR_12]: 'h:mm a',
    [TimeFormatSetting.HOUR_24]: 'HH:mm'
  },
  [DateStyle.DATE_ONLY_SHORT_MONTH]: {
    [DateFormatSetting.DAY_MONTH_YEAR]: 'dd MMM yyyy',
    [DateFormatSetting.MONTH_DAY_YEAR]: 'MMM dd yyyy'
  },
  [DateStyle.WITH_DAY_OF_WEEK]: {
    [DateFormatSetting.DAY_MONTH_YEAR]: 'EEE dd/MM/yy',
    [DateFormatSetting.MONTH_DAY_YEAR]: 'EEE MM/dd/yy'
  },
  [DateStyle.FULL_FORMAT]: DateTimeStringFormat
};

export const useDateString = () => {
  const { selectedOrgSettings } = useSettings();
  const { selectedWorkspace } = useSelectedOrganization();

  const tz: TimezoneNames =
    convertTimezoneEnumToName(selectedWorkspace?.timezone || TimezoneSetting.UTC) || TimezoneNames.UTC;

  const formatDateString = useCallback(
    (dateString: TDateTimeString, dateStyle: DateStyle = DateStyle.DATE_ONLY, useWorkspaceTimezone = false) => {
      if (!dateString) return '';
      const dateFormat = selectedOrgSettings?.dateFormat || DateFormatSetting.MONTH_DAY_YEAR;
      const timeFormat = selectedOrgSettings?.timeFormat || TimeFormatSetting.HOUR_24;

      let formatString: string;

      // Check WITH_DAY_OF_WEEK as we need to combine date/time in accordance to use settings
      if (dateStyle === DateStyle.WITH_DAY_OF_WEEK) {
        const datePart =
          FormatString[DateStyle.WITH_DAY_OF_WEEK][
            dateFormat as keyof (typeof FormatString)[DateStyle.WITH_DAY_OF_WEEK]
          ];
        const timePart =
          FormatString[DateStyle.TIME_ONLY][timeFormat as keyof (typeof FormatString)[DateStyle.TIME_ONLY]];
        formatString = `${datePart} ${timePart}`;
      } else {
        const formatMap = FormatString[dateStyle];
        if (typeof formatMap === 'string') {
          formatString = formatMap;
        } else if (dateFormat && formatMap.hasOwnProperty(dateFormat)) {
          formatString = formatMap[dateFormat as keyof typeof formatMap];
        } else if (timeFormat && formatMap.hasOwnProperty(timeFormat)) {
          formatString = formatMap[timeFormat as keyof typeof formatMap];
        } else {
          throw new Error('Invalid Format Map Key');
        }
      }
      if (useWorkspaceTimezone) {
        dateString = convertUtcToTimezonedDate(dateString, tz);
      }

      const date = new Date(dateString);
      return format(date, formatString);
    },
    [selectedOrgSettings, tz]
  );

  return { formatDateString };
};
